import React, { useState } from "react";
import {
  Ticket,
  Users,
  Banknote,
  Trophy,
  ArrowLeft,
  DollarSign,
  Wallet,
  Coins,
  Scale,
  Pencil,
} from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { Link } from "Common/SafeLink";
import { useSlotDetails } from "./useGameSlot";
import TicketsPerGameSlot from "./ticketsPerGameSlot";
import WinnerPerGameSlot from "./WinnerPerGameSlot";

const BreadCrumb: React.FC<{ title: React.ReactNode }> = ({ title }) => {
  return <div>{title}</div>;
};

const DetailsPerGameSlot = () => {
  const navigate = useNavigate();
  const { gameSlotId } = useParams<{ gameSlotId: string }>();
  const { isLoading, details, error } = useSlotDetails(gameSlotId);
  const [activeTab, setActiveTab] = useState("tickets");

  if (isLoading) return <p className="dark:text-white">Loading...</p>;
  if (error)
    return <p className="text-red-500">Error loading game slot details.</p>;

  const formatDrawDate = (drawDate: any) => {
    if (!drawDate) return "N/A";
    if (typeof drawDate === "string") return new Date(drawDate).toLocaleString();
    if (Array.isArray(drawDate) && drawDate.length >= 5) {
      const [year, month, day, hour, minute] = drawDate;
      return new Date(year, month - 1, day, hour ?? 0, minute ?? 0).toLocaleString();
    }
    return "N/A";
  };

  return (
    <div className="py-2 dark:bg-gray-900 dark:text-white min-h-screen">
      <BreadCrumb
        title={
          <button
            className="flex items-center gap-2 my-5 dark:text-white"
            onClick={() => navigate("/game-slots")}
          >
            <ArrowLeft className="w-5 h-5" />
            Slot Details
          </button>
        }
      />

      {/* Slot Info Section */}
      {details && (
        <div className="dark:bg-gray-800 p-4 space-y-3 rounded-lg shadow-md mb-6">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-xl font-semibold">
              {details?.gameslotdto?.slotTitle}
            </h2>
            {details?.gameslotdto?.soldTickets <= 0 && gameSlotId && (
              <Link
                to={`/game-slots/edit/${gameSlotId}`}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium rounded-md bg-custom-500 text-white hover:bg-custom-600"
              >
                <Pencil className="w-4 h-4" />
                Edit Slot
              </Link>
            )}
          </div>
          <p className="text-gray-400 text-sm">
            Slot ID: {details?.gameslotdto?.id ?? gameSlotId}
          </p>
          <p className="text-gray-400 text-sm">
            Reference ID: {details?.gameslotdto?.referenceId}
          </p>
          <p className="text-gray-400 text-sm">
            Draw Date: {formatDrawDate(details?.gameslotdto?.drawDate)}
          </p>
          <p
            className={`text-sm font-semibold ${
              details?.gameslotdto?.state === "CLOSED"
                ? "text-red-500"
                : "text-green-500"
            }`}
          >
            Status: {details?.gameslotdto?.state}
          </p>
          {details?.gameslotdto?.winnerAmounts &&
            Array.isArray(details.gameslotdto.winnerAmounts) &&
            details.gameslotdto.winnerAmounts.length > 0 && (
              <div className="pt-2 mt-2 border-t border-gray-600">
                <p className="text-sm font-medium text-gray-300 mb-1">Winner amounts (per position)</p>
                <ul className="list-disc list-inside text-sm text-gray-400 space-y-0.5">
                  {details.gameslotdto.winnerAmounts.map((amount: number, i: number) => (
                    <li key={i}>
                      Winner {i + 1}: ₦{Number(amount).toLocaleString()}
                    </li>
                  ))}
                </ul>
                <p className="text-sm text-gray-400 mt-1">
                  Total: ₦
                  {details.gameslotdto.winnerAmounts
                    .reduce((s: number, a: any) => s + (Number(a) || 0), 0)
                    .toLocaleString()}
                </p>
              </div>
            )}
        </div>
      )}

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        {details &&
          [
            {
              icon: <Ticket className="w-6 h-6 text-blue-500" />, // Represents tickets
              label: "Total Tickets",
              value:
                details?.gameslotdto?.totalNumberOfTickets?.toLocaleString() ||
                0,
            },
            {
              icon: <Users className="w-6 h-6 text-green-500" />, // Represents multiple people (sold tickets)
              label: "Sold Tickets",
              value: details?.gameslotdto?.soldTickets?.toLocaleString() || 0,
            },
            {
              icon: <Ticket className="w-6 h-6 text-red-500" />, // Represents remaining tickets
              label: "Remaining Tickets",
              value:
                (details?.gameslotdto?.totalNumberOfTickets &&
                  (
                    details?.gameslotdto?.totalNumberOfTickets -
                    details?.gameslotdto?.soldTickets
                  ).toLocaleString()) ||
                0,
            },

            {
              icon: <Trophy className="w-6 h-6 text-purple-500" />, // Represents winning
              label: "Winner Percentage",
              value: `${details?.gameslotdto?.winnerPercentage || 0}%`,
            },
            {
              icon: <Coins className="w-6 h-6 text-yellow-700" />, // Represents total winnings
              label: "Total Wins Amount",
              value: `₦${details?.totalWinAmount?.toLocaleString() || 0}`,
            },
            {
              icon: <DollarSign className="w-6 h-6 text-green-500" />, // Represents revenue
              label: "Gross Revenue",
              value: `₦${details?.grossRevenue?.toLocaleString() || 0}`,
            },
            {
              icon: <Wallet className="w-6 h-6 text-yellow-700" />, // Represents net revenue (profit)
              label: "Net Revenue",
              value: `₦${details?.netRevenue?.toLocaleString() || 0}`,
            },
            {
              icon: <Banknote className="w-6 h-6 text-red-500" />, // Represents expenses/charges
              label: "Charges",
              value: `₦${details?.charges?.toLocaleString() || 0}`,
            },
            {
              icon: <Scale className="w-6 h-6 text-blue-500" />, // Represents payout ratio (balance)
              label: "Payout Ratio",
              value: `${details?.payoutRatio?.toLocaleString() || 0}%`,
            },
          ].map((stat, index) => (
            <div
              key={index}
              className="dark:bg-gray-800 p-4 rounded-lg flex items-center gap-4 shadow-md"
            >
              {stat.icon}
              <div>
                <p className="text-gray-400 text-sm">{stat.label}</p>
                <p className="text-lg font-semibold">{stat.value}</p>
              </div>
            </div>
          ))}
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-4 mt-6 border-b pb-2 dark:border-gray-700">
        <button
          className={`px-4 py-2 ${
            activeTab === "tickets"
              ? "border-b-2 border-blue-500 font-bold text-blue-400"
              : "text-gray-500"
          }`}
          onClick={() => setActiveTab("tickets")}
        >
          Tickets
        </button>
        <button
          className={`px-4 py-2 ${
            activeTab === "winners"
              ? "border-b-2 border-blue-500 font-bold text-blue-400"
              : "text-gray-500"
          }`}
          onClick={() => setActiveTab("winners")}
        >
          Winners
        </button>
      </div>

      {/* Component Rendering Based on Selection */}
      <div className="mt-4">
        {activeTab === "tickets" ? (
          <TicketsPerGameSlot />
        ) : (
          <WinnerPerGameSlot gameSlotId={gameSlotId} />
        )}
      </div>
    </div>
  );
};

export default DetailsPerGameSlot;
